type LoadingScreenProps = {
  background?: string;
  textColor?: string;
};

/**
 * LoadingScreen - Context-aware loading screen that matches the theme of the page
 *
 * PRIORITY ORDER:
 * 1. Props (if explicitly provided)
 * 2. currentPageTheme (from NavigationContext - most accurate)
 * 3. siteCustomization.computedColors (transformed site theme)
 * 4. siteCustomization.baseColors (fallback)
 * 5. CSS variables (set by PreInit script)
 * 6. Hardcoded defaults
 *
 * IMPORTANT: Uses synchronous cache read on first render (NO useState, NO useEffect)
 */
export default function LoadingScreen({
  background,
  textColor
}: LoadingScreenProps = {}) {
  
  // SYNCHRONOUS cache read on first render - computed once using IIFE
  const theme = (() => {
    console.log('[LoadingScreen] 🎬 RENDER STARTED - props:', { background, textColor });
    console.log('[LoadingScreen] 🎬 Current pathname:', window.location.pathname);
    console.log('[LoadingScreen] 🎬 SessionStorage keys:', Object.keys(sessionStorage).filter(k => k.includes('Theme') || k.includes('theme')));

    // If props provided, use them
    if (background && textColor) {
            return { background, textColor, source: 'props' };
    }

    // PRIORITY 1: Try currentPageTheme (from NavigationContext)
    try {
      const currentPageTheme = sessionStorage.getItem('currentPageTheme');
      console.log('[LoadingScreen] 🔍 Raw currentPageTheme from sessionStorage:', currentPageTheme);
      if (currentPageTheme) {
        const parsed = JSON.parse(currentPageTheme);
        console.log('[LoadingScreen] 🔍 Parsed currentPageTheme:', parsed);

        if (parsed.colors) {
          const bg = `linear-gradient(135deg, ${parsed.colors.primaryColor}, ${parsed.colors.secondaryColor})`;
          const txt = parsed.colors.textColor || '#ffffff';

          console.log('[LoadingScreen] ✅ Using currentPageTheme:', { type: parsed.type, colors: parsed.colors });
          return {
            background: bg,
            textColor: txt,
            source: `${parsed.type}-page`
          };
        }
      }
    } catch (error) {
      console.error('❌ [LoadingScreen] Failed to read currentPageTheme:', error);
    }

    // PRIORITY 1.5: If on profile/community page, check specific theme caches directly
    // This handles hard reload (F5) where currentPageTheme might be cleared
    try {
      const pathname = window.location.pathname;
      console.log('[LoadingScreen] 🔍 Checking pathname:', pathname);

      // Profile page: /u/:username or /user/:username
      if (pathname.startsWith('/u/') || pathname.startsWith('/user/') || pathname.startsWith('/profile/')) {
        console.log('[LoadingScreen] 🔍 Detected profile page, checking profile theme caches...');
        const keys = Object.keys(sessionStorage);
        const profileThemeKey = keys.find(key => key.startsWith('profileTheme_'));

        if (profileThemeKey) {
          const cached = sessionStorage.getItem(profileThemeKey);
          if (cached) {
            const parsed = JSON.parse(cached);
            console.log('[LoadingScreen] 🔍 Found profile theme cache:', parsed);

            const bg = `linear-gradient(135deg, ${parsed.primaryColor}, ${parsed.secondaryColor || parsed.primaryColor})`;
            const txt = parsed.textColor || '#ffffff';

            console.log('[LoadingScreen] ✅ Using profile theme cache directly');
            return {
              background: bg,
              textColor: txt,
              source: 'profile-cache-direct'
            };
          }
        }
      }

      // Community page: /community/:slug
      if (pathname.startsWith('/community/')) {
        console.log('[LoadingScreen] 🔍 Detected community page, checking community theme caches...');
        const keys = Object.keys(sessionStorage);
        const communityThemeKey = keys.find(key => key.startsWith('communityTheme_'));

        if (communityThemeKey) {
          const cached = sessionStorage.getItem(communityThemeKey);
          if (cached) {
            const parsed = JSON.parse(cached);
            console.log('[LoadingScreen] 🔍 Found community theme cache:', parsed);

            const bg = `linear-gradient(135deg, ${parsed.primaryColor}, ${parsed.secondaryColor || parsed.primaryColor})`;
            const txt = parsed.textColor || '#ffffff';

            console.log('[LoadingScreen] ✅ Using community theme cache directly');
            return {
              background: bg,
              textColor: txt,
              source: 'community-cache-direct'
            };
          }
        }
      }
    } catch (error) {
      console.error('❌ [LoadingScreen] Failed to check direct theme caches:', error);
    }

    // PRIORITY 2: Try siteCustomization (prioritize computedColors over baseColors)
    try {
      const siteCustomization = sessionStorage.getItem('siteCustomization');
      if (siteCustomization) {
        const parsed = JSON.parse(siteCustomization);

        
        // CRITICAL: Use computedColors first (has color transformation applied)
        const colors = parsed.computedColors || parsed.baseColors;
        const source = parsed.computedColors ? 'site-computed' : 'site-base';

        if (colors?.primaryColor) {
          const bg = `linear-gradient(135deg, ${colors.primaryColor}, ${colors.secondaryColor})`;
          const txt = colors.textColor || '#ffffff';

          
          return {
            background: bg,
            textColor: txt,
            source
          };
        }
      }
    } catch (error) {
      console.error('❌ [LoadingScreen] Failed to read siteCustomization:', error);
    }

    // PRIORITY 3: Fall back to CSS variables (set by PreInit script)
    console.warn('[LoadingScreen] ⚠️ No theme found in sessionStorage! Using CSS variables fallback');
        return {
      background: 'var(--app-bg, linear-gradient(135deg, #1a1a1a, #2a2a2a))',
      textColor: 'var(--text-color, #ffffff)',
      source: 'css-variables'
    };
  })();


  console.log('[LoadingScreen] 🎨 Using theme from:', theme.source, 'background:', theme.background, 'textColor:', theme.textColor);
  return (
    <div
      className="min-h-screen flex items-center justify-center"
      style={{
        background: theme.background,
        color: theme.textColor,
        // Ensure no transitions cause flashing
        transition: 'none'
      }}
    >
      <div
        className="text-xl"
        style={{
          color: theme.textColor,
          transition: 'none'
        }}
      >
        Loading...
      </div>
    </div>
  );
}
